
import { useState } from 'react';
import { Video, User, PlusCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/hooks/useAuth';
import TrialSessionBooking, { TrialSessionDetails } from '@/components/client/TrialSessionBooking';
import SubscriptionBooking, { SubscriptionDetails, SubscriptionBookingProps } from '@/components/client/SubscriptionBooking';
import { TimeSlot, SpecificDateAvailability } from '@/components/therapists/calendar/types';
import { Link, useNavigate } from 'react-router-dom'; 
import { 
  sendClientSessionBookedEmail, 
  sendTherapistSessionBookedEmail,
  sendClientSubscriptionEmail,
  sendTherapistSubscriptionEmail
} from '@/services/emailService';

type TherapistBookingCardProps = {
  therapistId: string;
  therapistName: string;
  sessionPrice: number;
  trialPrice: number;
  hasInPerson: boolean;
  weeklyAvailability: TimeSlot[];
  specificDates: SpecificDateAvailability[];
  existingAppointments: Date[];
  userBalance: number;
};

const TherapistBookingCard = ({
  therapistId,
  therapistName,
  sessionPrice,
  trialPrice,
  hasInPerson,
  weeklyAvailability,
  specificDates,
  existingAppointments,
  userBalance
}: TherapistBookingCardProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedSessionType, setSelectedSessionType] = useState<'online' | 'inperson'>('online');
  
  const handleTrialBooking = async (details: TrialSessionDetails) => {
    console.log('Trial session booked:', details);
    
    // Check if user is logged in
    if (!user) {
      toast({
        title: "Inicio de sesión requerido",
        description: "Por favor inicia sesión para reservar una sesión",
        variant: "destructive",
      });
      navigate('/auth');
      return;
    }
    // In a real app, this would make an API call to book the session
    // and deduct the balance
    
    // For demo purposes, assume these values would come from user context or session storage
    const clientEmail = "client@example.com";
    const clientFirstName = "John";
    const therapistEmail = "therapist@example.com";
    const therapistFirstName = "Dr. Smith";
    
    // Format date and time for email
    const formattedDate = details.date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    // Send confirmation emails to both parties
    await Promise.all([
      sendClientSessionBookedEmail(
        clientEmail,
        clientFirstName,
        therapistName,
        formattedDate,
        details.timeSlot,
        selectedSessionType
      ),
      sendTherapistSessionBookedEmail(
        therapistEmail,
        therapistFirstName,
        clientFirstName,
        formattedDate,
        details.timeSlot,
        selectedSessionType
      )
    ]);
    
    toast({
      title: "Trial session booked",
      description: `Your session with ${therapistName} has been scheduled for ${details.date.toLocaleDateString()} at ${details.timeSlot}. Check your email for confirmation.`
    });
  };
  
  const handleSubscribe = async (subscription: SubscriptionDetails) => {
    console.log('Subscription created:', subscription);
    // In a real app, this would make an API call to create the subscription
    // and deduct the balance
    
    // For demo purposes, assume these values would come from user context or session storage
    const clientEmail = "client@example.com";
    const clientFirstName = "John";
    const therapistEmail = "therapist@example.com";
    const therapistFirstName = "Dr. Smith";
    
    // Format start date for email
    const formattedStartDate = subscription.startDate.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    // Send confirmation emails to both parties
    await Promise.all([
      sendClientSubscriptionEmail(
        clientEmail,
        clientFirstName,
        therapistName,
        subscription.frequency,
        formattedStartDate
      ),
      sendTherapistSubscriptionEmail(
        therapistEmail,
        therapistFirstName,
        clientFirstName,
        subscription.frequency,
        formattedStartDate
      )
    ]);
    
    toast({
      title: "Subscription confirmed",
      description: `Your ${subscription.frequency} sessions with ${therapistName} have been scheduled. Check your email for details.`
    });
  };

  const handleAddFunds = () => {
    if (!user) {
      toast({
        title: "Login required",
        description: "Please log in or sign up to add funds to your account.",
        variant: "default"
      });
      navigate('/auth', { state: { redirectAfterLogin: window.location.pathname } });
      return;
    }
    navigate('/client-portal');
  };
  
  return (
    <Card className="border bg-white shadow-sm">
      <CardHeader className="px-4 pt-4 pb-2">
        <CardTitle className="text-lg">Book a Session</CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        <div className="space-y-4">
          {/* Session type buttons (online/in-person) */}
          <div className="flex justify-between mb-3">
            <Button 
              variant={selectedSessionType === 'online' ? 'default' : 'outline'}
              size="sm"
              className="flex-1 mr-1 text-xs sm:text-sm"
              onClick={() => setSelectedSessionType('online')}
            >
              <Video className="mr-1 h-3 w-3 sm:h-4 sm:w-4" />
              Online
            </Button>
            
            <Button 
              variant={selectedSessionType === 'inperson' ? 'default' : 'outline'}
              size="sm"
              className="flex-1 ml-1 text-xs sm:text-sm"
              disabled={!hasInPerson}
              onClick={() => setSelectedSessionType('inperson')}
            >
              <User className="mr-1 h-3 w-3 sm:h-4 sm:w-4" />
              In Person
            </Button>
          </div>
          
          {/* Session pricing */}
          <div className="mb-4">
            <p className="text-xs text-muted-foreground mb-1">Session Price:</p>
            <div className="flex items-baseline">
              <span className="text-xl font-bold">
                ${selectedSessionType === 'online' ? (sessionPrice - 10).toFixed(2) : sessionPrice.toFixed(2)}
              </span>
              <span className="text-xs text-muted-foreground ml-1">/ session</span>
            </div>
            {selectedSessionType === 'online' && (
              <p className="text-xs text-green-600 mt-1">
                Online discount applied
              </p>
            )}
          </div>
          
          {/* Balance section */}
          <div className="border-t pt-4">
            <div className="flex justify-between items-center mb-3">
              <p className="font-medium text-sm">Your balance: ${userBalance.toFixed(2)}</p>
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center text-xs"
                onClick={handleAddFunds}
              >
                <PlusCircle className="mr-1 h-3 w-3" />
                Add Funds
              </Button>
            </div>
            
            {userBalance < trialPrice && (
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-4">
                <p className="text-sm text-amber-800 mb-2">
                  Your balance is too low to book a session.
                </p>
                <Button 
                  variant="default" 
                  size="sm" 
                  className="w-full"
                  onClick={handleAddFunds}
                >
                  <PlusCircle className="mr-1 h-4 w-4" />
                  Add Funds Now
                </Button>
              </div>
            )}
            
            {/* Booking components */}
            <div className="space-y-3">
              <TrialSessionBooking 
                therapistId={therapistId}
                therapistName={therapistName}
                trialPrice={selectedSessionType === 'online' ? trialPrice - 5 : trialPrice}
                weeklyAvailability={weeklyAvailability}
                specificDates={specificDates}
                existingAppointments={existingAppointments}
                currentBalance={userBalance}
                onBook={handleTrialBooking}
                onAddFunds={handleAddFunds}
              />
              
              <SubscriptionBooking 
                therapistId={therapistId}
                therapistName={therapistName}
                sessionPrice={selectedSessionType === 'online' ? sessionPrice - 10 : sessionPrice}
                weeklyAvailability={weeklyAvailability}
                specificDates={specificDates}
                existingAppointments={existingAppointments}
                currentBalance={userBalance}
                onSubscribe={handleSubscribe}
                onAddFunds={handleAddFunds}
              />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TherapistBookingCard;
